import React, {
  Dispatch, ReactElement, ReactNode, SetStateAction, useState,
} from 'react';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Col, ErrorMessage,
  FormGroup,
  Heading,
  Input,
  LoaderButton,
  Row,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
} from 'idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import {
  ConfirmModal,
  TextEditor,
  Tooltip,
  useResponsiveContext,
} from 'slovlex-idsk-react-components';
import {
  SpravaOUcastiVerejnostiFormData,
} from '../../../../model/sprievodnyDokument/spravaoUcastiVerejnosti/SpravaOUcastiVerejnostiFormData';
import './spravaoUcastiVerejnosti.scss';
import { Subjekty } from '../../../../model/sprievodnyDokument/spravaoUcastiVerejnosti/Subjekty';
import { useClient, useClientStatusContext } from '../../../../context/ClientStatusContext';
import LegislativnyMaterialDetail
  from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import {
  tooltip1,
  tooltip2,
  tooltip3,
  tooltip4,
} from '../../../../constants/Tooltips';
import routes from '../../../../routing/routes';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';
import { ClientResponse } from '../../../../model/ClientResponse';
import Uuid from '../../../../model/Uuid';
import FormCheckBox from '../component/FormCheckBox';
import useFieldErrorMessages from '../../../useFieldErrorMessages';
import { pouzivatelIsAdmin } from '../../../../function/permissions';
import { useUserContext } from '../../../../context/UserContext';

export interface PointProps {
  clearFieldErrorMessage: (fieldName: string) => void;
  getFieldErrorMessage: (fieldName: string) => string;
  formData: SpravaOUcastiVerejnostiFormData,
  setFormData: Dispatch<SetStateAction<SpravaOUcastiVerejnostiFormData>>,
}

export interface PointPropsWithInitData extends PointProps {
  initFormData: SpravaOUcastiVerejnostiFormData,
}

export const PointOne = ({
  clearFieldErrorMessage, getFieldErrorMessage, initFormData, formData, setFormData,
}: PointPropsWithInitData): ReactElement => (
  <section className="sprava-o-ucasti-verejnosti-section">
    <Heading size="m">1. Spôsob zapojenia verejnosti do tvorby právneho predpisu</Heading>
    <Table>
      <Tbody>
        <CheckboxTableRow title="Informovanie - vyplnia sa body 2 a 3">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="informovanie" />
        </CheckboxTableRow>
        <CheckboxTableRow title="Prerokovanie - vyplnia sa body 2 až 11">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="prerokovanie" />
        </CheckboxTableRow>
      </Tbody>
    </Table>
    <FormGroup error={getFieldErrorMessage && !!getFieldErrorMessage('odovodnenieZapojenieVerejnosti')}>
      <Row>
        <Col>
          <Tooltip tooltipText={tooltip1}>
            <Text className="sprava-o-ucasti-verejnosti__label">Odôvodnenie</Text>
          </Tooltip>
          {getFieldErrorMessage('odovodnenieZapojenieVerejnosti')
            && <ErrorMessage message={getFieldErrorMessage('odovodnenieZapojenieVerejnosti')} />}
          <TextEditor
            initialState={initFormData.odovodnenieZapojenieVerejnosti}
            onChange={(data: string) => {
              setFormData({ ...formData, odovodnenieZapojenieVerejnosti: data });
              clearFieldErrorMessage('odovodnenieZapojenieVerejnosti');
            }}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

export const PointTwo = ({
  clearFieldErrorMessage, getFieldErrorMessage, formData, setFormData,
}: PointProps): ReactElement => (
  <section className="sprava-o-ucasti-verejnosti-section">
    <Heading size="m">2. Spôsob informovania verejnosti o začatí tvorby právneho predpisu
    </Heading>
    <Table>
      <Tbody>
        <CheckboxTableRow title="Predbežná informácia">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="predbeznaInformacia" />
        </CheckboxTableRow>
        <CheckboxTableRow title="Legislatívny zámer">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="legislativnyZamer" />
        </CheckboxTableRow>
        <Tr>
          <Td>
            <div className="sprava-o-ucasti-verejnosti__ine-container">
              <Tooltip tooltipText={tooltip1}>
                <Text className="sprava-o-ucasti-verejnosti__label">Iné:</Text>
              </Tooltip>
              <Input
                error={getFieldErrorMessage && getFieldErrorMessage('ineSposobInformovaniaVerejnostiText')}
                className="sprava-o-ucasti-verejnosti__input-ine"
                width="three-quarters"
                value={formData.ineSposobInformovaniaVerejnostiText}
                onChange={e => {
                  setFormData({
                    ...formData,
                    ineSposobInformovaniaVerejnostiText: e.target.value,
                  });
                  clearFieldErrorMessage('ineSposobInformovaniaVerejnostiText');
                }}
                maxLength={256}
                type="text"
              />
            </div>
          </Td>
          <Td>
            <FormCheckBox formData={formData} setFormData={setFormData} prop="ineSposobInformovaniaVerejnosti" />
          </Td>
        </Tr>
      </Tbody>
    </Table>
  </section>
);

export const PointThree = ({
  clearFieldErrorMessage, getFieldErrorMessage, formData, setFormData,
}: PointProps): ReactElement => (
  <section className="sprava-o-ucasti-verejnosti-section">
    <Heading size="m">3. Informácie poskytnuté verejnosti</Heading>
    <Table>
      <Tbody>
        <CheckboxTableRow title="O probléme, ktorý má právny predpis riešiť">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="oprobleme" />
        </CheckboxTableRow>
        <CheckboxTableRow title="O spôsobe zapojenia verejnosti do tvorby právneho predpisu">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="osposobeZapojenia" />
        </CheckboxTableRow>
        <CheckboxTableRow title="O časovom rámci tvorby právneho predpisu">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="ocasovomRamci" />
        </CheckboxTableRow>
        <CheckboxTableRow title="O procese tvorby právneho predpisu">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="oprocese" />
        </CheckboxTableRow>
        <CheckboxTableRow title="O spôsobe naloženia s vyjadreniami a návrhmi verejnosti">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="osposobeNalozenia" />
        </CheckboxTableRow>
        <Tr>
          <Td>
            <div className="sprava-o-ucasti-verejnosti__ine-container">
              <Tooltip tooltipText={tooltip1}>
                <Text className="sprava-o-ucasti-verejnosti__label">Iné:</Text>
              </Tooltip>
              <Input
                error={getFieldErrorMessage && getFieldErrorMessage('ineInformacieVerejnostiText')}
                className="sprava-o-ucasti-verejnosti__input-ine"
                width="three-quarters"
                value={formData.ineInformacieVerejnostiText}
                onChange={e => {
                  setFormData({
                    ...formData,
                    ineInformacieVerejnostiText: e.target.value,
                  });
                  clearFieldErrorMessage('ineInformacieVerejnostiText');
                }}
                maxLength={256}
                type="text"
              />
            </div>
          </Td>
          <Td>
            <FormCheckBox formData={formData} setFormData={setFormData} prop="ineInformacieVerejnosti" />
          </Td>
        </Tr>
      </Tbody>
    </Table>
  </section>
);

const PointFour = ({
  clearFieldErrorMessage, getFieldErrorMessage, initFormData, formData, setFormData,
}: PointPropsWithInitData): ReactElement => {
  const { isSmallScreen } = useResponsiveContext();
  return (
    <section className="sprava-o-ucasti-verejnosti-section">
      <Heading size="m">4. Forma prerokovania s verejnosťou</Heading>
      <Table>
        <Tbody>
          <CheckboxTableRow title="Osobne">
            <FormCheckBox formData={formData} setFormData={setFormData} prop="formaOsobne" />
          </CheckboxTableRow>
          <CheckboxTableRow title="Ústne">
            <FormCheckBox formData={formData} setFormData={setFormData} prop="formaUstne" />
          </CheckboxTableRow>
          <CheckboxTableRow title="Písomne">
            <FormCheckBox formData={formData} setFormData={setFormData} prop="formaPisomne" />
          </CheckboxTableRow>
          <Tr>
            <Td>
              <div className="sprava-o-ucasti-verejnosti__ine-container">
                <Tooltip
                  tooltipText={tooltip1}
                ><Text className="sprava-o-ucasti-verejnosti__label">Inou formou:</Text>
                </Tooltip>
                <Input
                  error={getFieldErrorMessage && getFieldErrorMessage('formaIneText')}
                  className="sprava-o-ucasti-verejnosti__input-ine prerokovanie-verejnostou__input-ine"
                  value={formData.formaIneText}
                  width={isSmallScreen ? 'full' : 'three-quarters'}
                  onChange={e => {
                    setFormData({
                      ...formData,
                      formaIneText: e.target.value,
                    });
                    clearFieldErrorMessage('formaIneText');
                  }}
                  maxLength={256}
                  type="text"
                />
              </div>
            </Td>
            <Td>
              <FormCheckBox formData={formData} setFormData={setFormData} prop="formaIne" />
            </Td>
          </Tr>
        </Tbody>
      </Table>
      <FormGroup
        error={getFieldErrorMessage && !!getFieldErrorMessage('formaOdovodnenie')}
      >
        <Row>
          <Col>
            <Tooltip tooltipText={tooltip1}>
              <Text className="sprava-o-ucasti-verejnosti__label">Odôvodnenie</Text>
            </Tooltip>
            {getFieldErrorMessage('formaOdovodnenie')
            && <ErrorMessage message={getFieldErrorMessage('formaOdovodnenie')} />}
            <TextEditor
              initialState={initFormData.formaOdovodnenie}
              onChange={(data: string) => {
                setFormData({
                  ...formData,
                  formaOdovodnenie: data,
                });
                clearFieldErrorMessage('formaOdovodnenie');
              }}
              maxLength={2048}
              showRemaining
            />
          </Col>
        </Row>
      </FormGroup>
    </section>
  );
};

interface CheckboxTableRowProps {
  title: ReactNode;
  children: ReactNode;
}

function CheckboxTableRow({ title, children }: CheckboxTableRowProps): ReactElement {
  return <Tr><Td>{title}</Td><Td>{children}</Td></Tr>;
}

const PointFive = ({
  getFieldErrorMessage, clearFieldErrorMessage, formData, setFormData,
}: PointProps): ReactElement => (
  <section className="sprava-o-ucasti-verejnosti-section">
    <Heading size="m">5. Spôsoby prerokovania s verejnosťou</Heading>
    <Table>
      <Tbody>
        <CheckboxTableRow title="Pracovná skupina">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="pracovnaSkupina" />
        </CheckboxTableRow>
        <CheckboxTableRow title="Konferencia">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="konferencia" />
        </CheckboxTableRow>
        <CheckboxTableRow title={(
          <Tooltip tooltipText={tooltip2}>Diskusia k legislatívnemu procesu</Tooltip>
      )}
        >
          <FormCheckBox formData={formData} setFormData={setFormData} prop="diskusiaKLegislativnemuProcesu" />
        </CheckboxTableRow>
        <CheckboxTableRow title={(
          <Tooltip tooltipText={tooltip3}>Konzultácia</Tooltip>
      )}
        >
          <FormCheckBox formData={formData} setFormData={setFormData} prop="konzultacia" />
        </CheckboxTableRow>
        <CheckboxTableRow title="Pripomienkovanie">
          <FormCheckBox formData={formData} setFormData={setFormData} prop="pripomienkovanie" />
        </CheckboxTableRow>
        <Tr>
          <Td>
            <div className="sprava-o-ucasti-verejnosti__ine-container">
              <Tooltip tooltipText={tooltip1}>
                <Text className="sprava-o-ucasti-verejnosti__label">Iné:</Text>
              </Tooltip>
              <Input
                error={getFieldErrorMessage && getFieldErrorMessage('inePrerokovanieVerejnostouText')}
                className="sprava-o-ucasti-verejnosti__input-ine"
                width="three-quarters"
                value={formData.inePrerokovanieVerejnostouText}
                onChange={e => {
                  setFormData({
                    ...formData,
                    inePrerokovanieVerejnostouText: e.target.value,
                  });
                  clearFieldErrorMessage('inePrerokovanieVerejnostouText');
                }}
                maxLength={256}
                type="text"
              />
            </div>
          </Td>
          <Td>
            <FormCheckBox formData={formData} setFormData={setFormData} prop="inePrerokovanieVerejnostou" />
          </Td>
        </Tr>
      </Tbody>
    </Table>
  </section>
);

export interface SubjectTableProps {
  subjekty: Subjekty;
  setSubjekty: Dispatch<Subjekty>,
}

export const SubjectsTable = ({ subjekty, setSubjekty }: SubjectTableProps): ReactElement => (
  <section className="sprava-o-ucasti-verejnosti-section">
    <Table>
      <Thead>
        <Tr>
          <Th>Okruh subjektov</Th>
          <Th>Počet subjektov</Th>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td>Záujmové združenia subjektov územnej samosprávy</Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={subjekty.zaujmoveZdruzenia ?? ''}
              onChange={(e) => setSubjekty({
                ...subjekty,
                zaujmoveZdruzenia: (e.target.validity.valid ? e.target.value : subjekty.zaujmoveZdruzenia),
              })}
              maxLength={256}
            />
          </Td>
        </Tr>
        <Tr>
          <Td>Podnikatelia a záujmové združenia podnikateľov</Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={subjekty.podnikatelia ?? ''}
              onChange={(e) => setSubjekty({
                ...subjekty,
                podnikatelia: (e.target.validity.valid ? e.target.value : subjekty.podnikatelia),
              })}
              maxLength={256}
            />
          </Td>
        </Tr>
        <Tr>
          <Td>
            <Tooltip tooltipText={tooltip4}>Mimovládne neziskové organizácie</Tooltip>
          </Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={subjekty.mimovladneANeziskoveOrg ?? ''}
              onChange={e => setSubjekty({
                ...subjekty,
                mimovladneANeziskoveOrg: (e.target.validity.valid ? e.target.value : subjekty.mimovladneANeziskoveOrg),
              })}
              maxLength={256}
            />
          </Td>
        </Tr>
        <Tr>
          <Td>Akademická a vedecká obec</Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={subjekty.akademickaAVedeckaObec ?? ''}
              onChange={e => setSubjekty({
                ...subjekty,
                akademickaAVedeckaObec: (e.target.validity.valid ? e.target.value : subjekty.akademickaAVedeckaObec),
              })}
              maxLength={256}
            />
          </Td>
        </Tr>
        <Tr>
          <Td>Cirkvi a náboženské spoločnosti</Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={subjekty.cirkvi ?? ''}
              onChange={e => setSubjekty({
                ...subjekty,
                cirkvi: (e.target.validity.valid ? e.target.value : subjekty.cirkvi),
              })}
              maxLength={256}
            />
          </Td>
        </Tr>
        <Tr>
          <Td>
            <div className="sprava-o-ucasti-verejnosti__ine-container">
              <Tooltip tooltipText={tooltip1}>
                <Text className="sprava-o-ucasti-verejnosti__label">Iné:</Text>
              </Tooltip>
              <Input
                className="sprava-o-ucasti-verejnosti__input-ine"
                width="three-quarters"
                type="text"
                value={subjekty.ineText}
                onChange={e => setSubjekty({ ...subjekty, ineText: e.target.value })}
                maxLength={256}
              />
            </div>
          </Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={subjekty.ine ?? ''}
              onChange={e => setSubjekty({
                ...subjekty,
                ine: (e.target.validity.valid ? e.target.value : subjekty.ine),
              })}
              maxLength={256}
            />
          </Td>
        </Tr>
      </Tbody>
    </Table>
  </section>
);

export const PointTen = ({
  getFieldErrorMessage, clearFieldErrorMessage, initFormData, formData, setFormData,
}: PointPropsWithInitData): ReactElement => (
  <section className="sprava-o-ucasti-verejnosti-section">
    <Heading size="m">10. Spôsob naloženia s vyjadreniami a návrhmi zapojených subjektov</Heading>
    <Table>
      <Thead>
        <Tr>
          <Th>Okruh subjektov</Th>
          <Th><div className="column-heading">Prevažne<br />akceptované&nbsp;/&nbsp;neakceptované</div></Th>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Td>Orgány verejnej správy </Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <div className="options-cell">
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="organyVerejnejSpravyAkceptovane"
                  checked={formData.organyVerejnejSpravy === undefined ? false : formData.organyVerejnejSpravy}
                  onChange={e => setFormData({
                    ...formData, organyVerejnejSpravy: e.target.checked ? true : undefined,
                  })}
                >/
                </Checkbox>
              </CheckboxGroup>
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="organyVerejnejSpravyNeakceptovane"
                  checked={formData.organyVerejnejSpravy === undefined ? false : !formData.organyVerejnejSpravy}
                  onChange={e => setFormData({
                    ...formData, organyVerejnejSpravy: e.target.checked ? false : undefined,
                  })}
                >&nbsp;
                </Checkbox>
              </CheckboxGroup>
            </div>
          </Td>
        </Tr>
        <Tr>
          <Td>Záujmové združenia subjektov územnej samosprávy</Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <div className="options-cell">
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="zaujmoveZdruzenia"
                  checked={formData.zaujmoveZdruzenia === undefined ? false : formData.zaujmoveZdruzenia}
                  onChange={e => setFormData({
                    ...formData, zaujmoveZdruzenia: e.target.checked ? true : undefined,
                  })}
                >/
                </Checkbox>
              </CheckboxGroup>
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="zaujmoveZdruzenia"
                  checked={formData.zaujmoveZdruzenia === undefined ? false : !formData.zaujmoveZdruzenia}
                  onChange={e => setFormData({
                    ...formData, zaujmoveZdruzenia: e.target.checked ? false : undefined,
                  })}
                >&nbsp;
                </Checkbox>
              </CheckboxGroup>
            </div>
          </Td>
        </Tr>
        <Tr>
          <Td>Podnikatelia a záujmové združenia podnikateľov</Td>
          <Td className="sprava-o-ucasti-verejnosti__number-input">
            <div className="options-cell">
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="podnikatelia"
                  checked={formData.podnikatelia === undefined ? false : formData.podnikatelia}
                  onChange={e => setFormData({
                    ...formData, podnikatelia: e.target.checked ? true : undefined,
                  })}
                >/
                </Checkbox>
              </CheckboxGroup>
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="podnikatelia"
                  checked={formData.podnikatelia === undefined ? false : !formData.podnikatelia}
                  onChange={e => setFormData({
                    ...formData, podnikatelia: e.target.checked ? false : undefined,
                  })}
                >&nbsp;
                </Checkbox>
              </CheckboxGroup>
            </div>
          </Td>
        </Tr>
        <Tr>
          <Td>
            <Tooltip tooltipText={tooltip4}>Mimovládne neziskové organizácie</Tooltip>
          </Td>
          <Td>
            <div className="options-cell">
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="mimovladneANeziskoveOrg"
                  checked={formData.mimovladneANeziskoveOrg === undefined ? false : formData.mimovladneANeziskoveOrg}
                  onChange={e => setFormData({
                    ...formData, mimovladneANeziskoveOrg: e.target.checked ? true : undefined,
                  })}
                >/
                </Checkbox>
              </CheckboxGroup>
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="mimovladneANeziskoveOrg"
                  checked={formData.mimovladneANeziskoveOrg === undefined ? false : !formData.mimovladneANeziskoveOrg}
                  onChange={e => setFormData({
                    ...formData, mimovladneANeziskoveOrg: e.target.checked ? false : undefined,
                  })}
                >&nbsp;
                </Checkbox>
              </CheckboxGroup>
            </div>
          </Td>
        </Tr>
        <Tr>
          <Td>Akademická a vedecká obec</Td>
          <Td>
            <div className="options-cell">
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="akademickaAVedeckaObec"
                  checked={formData.akademickaAVedeckaObec === undefined ? false : formData.akademickaAVedeckaObec}
                  onChange={e => setFormData({
                    ...formData, akademickaAVedeckaObec: e.target.checked ? true : undefined,
                  })}
                >/
                </Checkbox>
              </CheckboxGroup>
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="akademickaAVedeckaObec"
                  checked={formData.akademickaAVedeckaObec === undefined ? false : !formData.akademickaAVedeckaObec}
                  onChange={e => setFormData({
                    ...formData, akademickaAVedeckaObec: e.target.checked ? false : undefined,
                  })}
                >&nbsp;
                </Checkbox>
              </CheckboxGroup>
            </div>
          </Td>
        </Tr>
        <Tr>
          <Td>Cirkvi a náboženské spoločnosti</Td>
          <Td>
            <div className="options-cell">
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="cirkvi"
                  checked={formData.cirkvi === undefined ? false : formData.cirkvi}
                  onChange={e => setFormData({
                    ...formData, cirkvi: e.target.checked ? true : undefined,
                  })}
                >/
                </Checkbox>
              </CheckboxGroup>
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="cirkvi"
                  checked={formData.cirkvi === undefined ? false : !formData.cirkvi}
                  onChange={e => setFormData({
                    ...formData, cirkvi: e.target.checked ? false : undefined,
                  })}
                >&nbsp;
                </Checkbox>
              </CheckboxGroup>
            </div>
          </Td>
        </Tr>
        <Tr>
          <Td>
            <div className="sprava-o-ucasti-verejnosti__ine-container">
              <Tooltip tooltipText={tooltip1}>
                <Text className="sprava-o-ucasti-verejnosti__label">Iné:</Text>
              </Tooltip>
              <Input
                error={getFieldErrorMessage && getFieldErrorMessage('ineText')}
                className="sprava-o-ucasti-verejnosti__input-ine"
                width="three-quarters"
                type="text"
                value={formData.ineText}
                onChange={e => {
                  setFormData({
                    ...formData,
                    ineText: e.target.value,
                  });
                  clearFieldErrorMessage('ineText');
                }}
                maxLength={256}
              />
            </div>
          </Td>
          <Td>
            <div className="options-cell">
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="ine"
                  checked={formData.ine === undefined ? false : formData.ine}
                  onChange={e => setFormData({ ...formData, ine: e.target.checked ? true : undefined })}
                >/
                </Checkbox>
              </CheckboxGroup>
              <CheckboxGroup small>
                <Checkbox
                  className="sprava-o-ucasti-verejnosti__checkbox"
                  id="ine"
                  checked={formData.ine === undefined ? false : !formData.ine}
                  onChange={e => {
                    setFormData({ ...formData, ine: e.target.checked ? false : undefined });
                  }}
                >&nbsp;
                </Checkbox>
              </CheckboxGroup>
            </div>
          </Td>
        </Tr>
      </Tbody>
    </Table>
    <FormGroup
      error={getFieldErrorMessage && !!getFieldErrorMessage('sposobNalozeniaOdovodnenie')}
    >
      <Row>
        <Col>
          <Tooltip tooltipText={tooltip1}>
            <Text className="sprava-o-ucasti-verejnosti__label">Odôvodnenie</Text>
          </Tooltip>
          {getFieldErrorMessage('sposobNalozeniaOdovodnenie')
            && <ErrorMessage message={getFieldErrorMessage('sposobNalozeniaOdovodnenie')} />}
          <TextEditor
            initialState={initFormData.sposobNalozeniaOdovodnenie}
            onChange={(data: string) => {
              setFormData({
                ...formData,
                sposobNalozeniaOdovodnenie: data,
              });
              clearFieldErrorMessage('sposobNalozeniaOdovodnenie');
            }}
            maxLength={2048}
            showRemaining
          />
        </Col>
      </Row>
    </FormGroup>
  </section>
);

interface SpravaOUcastiProps {
  onSubmit: (uuid: Uuid, form: SpravaOUcastiVerejnostiFormData) => Promise<ClientResponse<void>>;
  material: LegislativnyMaterialDetail,
  initFormData: SpravaOUcastiVerejnostiFormData,
}

const SpravaoUcastiVerejnosti = ({
  onSubmit, material, initFormData,
}: SpravaOUcastiProps): ReactElement => {
  const [formData, setFormData] = useState<SpravaOUcastiVerejnostiFormData>(initFormData);
  const navigate = useNavigate();
  const { clearMessages, displaySuccess } = useSuccessMessageContext();
  const { clearStatus } = useClientStatusContext();
  const {
    type,
    year,
    index,
    stadiumUuid,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, stadiumUuid: string }>();
  const { pouzivatel } = useUserContext();

  const {
    wrapClient: submitSpravaoUcastiVerejnosti,
    status,
  } = useClient(onSubmit);

  const onSubmitForm = async (): Promise<void> => {
    await submitSpravaoUcastiVerejnosti(material.uuid, formData, stadiumUuid);
    navigate(pouzivatelIsAdmin(pouzivatel)
      ? routes.procesy.detail.sprievodneDokumenty.editAsAdmin.withParams(type, year, index, stadiumUuid)
      : routes.procesy.detail.sprievodneDokumenty.edit.withParams(type, year, index));
    displaySuccess('Dokument bol úspešne uložený');
  };

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();

  const clearStatusAndMessages = (): void => {
    clearStatus();
    clearMessages();
  };

  const { isSmallScreen } = useResponsiveContext();

  // @ts-ignore
  return (
    <section className="sprava-o-ucasti-verejnosti__form">
      <PointOne
        clearFieldErrorMessage={clearFieldErrorMessage}
        getFieldErrorMessage={getFieldErrorMessage}
        initFormData={initFormData}
        formData={formData}
        setFormData={setFormData}
      />
      <PointTwo
        clearFieldErrorMessage={clearFieldErrorMessage}
        getFieldErrorMessage={getFieldErrorMessage}
        formData={formData}
        setFormData={setFormData}
      />
      <PointThree
        clearFieldErrorMessage={clearFieldErrorMessage}
        getFieldErrorMessage={getFieldErrorMessage}
        formData={formData}
        setFormData={setFormData}
      />
      <PointFour
        clearFieldErrorMessage={clearFieldErrorMessage}
        getFieldErrorMessage={getFieldErrorMessage}
        initFormData={initFormData}
        formData={formData}
        setFormData={setFormData}
      />

      <PointFive
        clearFieldErrorMessage={clearFieldErrorMessage}
        getFieldErrorMessage={getFieldErrorMessage}
        formData={formData}
        setFormData={setFormData}
      />
      <section className="sprava-o-ucasti-verejnosti-section">
        <Heading size="m">6. Okruhy subjektov predkladateľom adresne vyzvané na účasť na tvorbe
          právneho predpisu
        </Heading>
        <SubjectsTable
          subjekty={formData.vyzvane}
          setSubjekty={(vyzvaneSubjekty) => setFormData({ ...formData, vyzvane: vyzvaneSubjekty })}
        />
        <FormGroup error={getFieldErrorMessage && !!getFieldErrorMessage('vyzvaneOdovodnenie')}>
          <Tooltip tooltipText={tooltip1}>
            <Text className="sprava-o-ucasti-verejnosti__label">Odôvodnenie</Text>
          </Tooltip>
          {getFieldErrorMessage('vyzvaneOdovodnenie')
            && <ErrorMessage message={getFieldErrorMessage('vyzvaneOdovodnenie')} />}
          <TextEditor
            initialState={initFormData.vyzvaneOdovodnenie}
            onChange={(data: string) => {
              setFormData({
                ...formData,
                vyzvaneOdovodnenie: data,
              });
              clearFieldErrorMessage('vyzvaneOdovodnenie');
            }}
            maxLength={2048}
            showRemaining
          />
        </FormGroup>
      </section>
      <section className="sprava-o-ucasti-verejnosti-section">
        <Heading size="m">7. Okruhy adresne vyzvaných subjektov aktívne zúčastnených na tvorbe
          právneho predpisu
        </Heading>
        <SubjectsTable
          subjekty={formData.zucastnene}
          setSubjekty={(zucastneneSubjekty) => setFormData({ ...formData, zucastnene: zucastneneSubjekty })}
        />
      </section>
      <section className="sprava-o-ucasti-verejnosti-section">
        <Heading size="m">8. Okruhy subjektov, ktoré prejavili záujem zúčastniť sa na tvorbe
          právneho predpisu z vlastnej iniciatívy
        </Heading>
        <SubjectsTable
          subjekty={formData.prejaviliZaujem}
          setSubjekty={(prejaviliZaujem) => setFormData({ ...formData, prejaviliZaujem })}
        />
      </section>
      <section className="sprava-o-ucasti-verejnosti-section">
        <Heading size="m">9. Okruhy iniciatívnych subjektov aktívne zúčastnených na tvorbe
          právneho predpisu
        </Heading>
        <SubjectsTable
          subjekty={formData.iniciativneZucastnene}
          setSubjekty={(iniciativneZucastneneSubjekty) => setFormData(
            { ...formData, iniciativneZucastnene: iniciativneZucastneneSubjekty },
          )}
        />
      </section>
      <PointTen
        clearFieldErrorMessage={clearFieldErrorMessage}
        getFieldErrorMessage={getFieldErrorMessage}
        initFormData={initFormData}
        formData={formData}
        setFormData={setFormData}
      />
      <FormGroup error={getFieldErrorMessage && !!getFieldErrorMessage('vyhodnotenieUcastiOdovodnenie')}>
        <section className="sprava-o-ucasti-verejnosti-section vyhodnotenieUcastiOdovodnenie">
          {isSmallScreen ? (
            <>
              <Heading size="m">
                11. Vyhodnotenie účasti verejnosti na tvorbe právneho predpisu predkladateľom
              </Heading>
              {/* eslint-disable-next-line react/no-children-prop */}
              <Tooltip tooltipText={tooltip1} children={null} />
            </>
          ) : (
            <Tooltip tooltipText={tooltip1}>
              <Heading size="m">
                11. Vyhodnotenie účasti verejnosti na tvorbe právneho predpisu predkladateľom
              </Heading>
            </Tooltip>
          )}
          {getFieldErrorMessage('vyhodnotenieUcastiOdovodnenie')
            && <ErrorMessage message={getFieldErrorMessage('vyhodnotenieUcastiOdovodnenie')} />}
          <TextEditor
            initialState={initFormData.vyhodnotenieUcastiOdovodnenie}
            onChange={(data: string) => {
              setFormData({
                ...formData,
                vyhodnotenieUcastiOdovodnenie: data,
              });
              clearFieldErrorMessage('vyhodnotenieUcastiOdovodnenie');
            }}
            maxLength={2048}
            showRemaining
          />
        </section>
      </FormGroup>
      <FormGroup className="sprava-o-ucasti-verejnosti__form-buttons">
        <ConfirmModal
          question="Naozaj si prajete uložiť formulár a vytvoriť sprievodný dokument?"
          description={'Ak upravujete existujúci formulár, budú údaje tohto formulára prepísané '
            + 'a výsledný dokument, ktorý vznikol na základe tohto formulára, bude nahradený.'}
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              loading={status.loading}
              type="submit"
              size="large"
              onClick={clearStatusAndMessages}
              disableOutsideClickWhileLoading
            >Uložiť
            </LoaderButton>
          )}
          doAfterConfirm={() => onSubmitForm()}
        />
        <Button
          className="sprava-o-ucasti-verejnosti__buttons"
          type="button"
          buttonStyle="third"
          size="large"
          disabled={status.loading}
          onClick={() => navigate(-1)}
        >Späť
        </Button>
      </FormGroup>
    </section>
  );
};

export default SpravaoUcastiVerejnosti;
